import React, { createContext, useContext, useMemo } from 'react'
import { useMyProfileWithModules } from '../api/api_hooks/user/myProfile'
import { resolveCHUConfig, DEFAULT_CHU_CONFIG } from '../config/chuConfig'
import type { CHUConfig } from '../config/chuConfig'

const CHUConfigContext = createContext<CHUConfig>(DEFAULT_CHU_CONFIG)

// Admin-only testing override: an app admin can force a specific CHU config
// (e.g. "chu nancy") to test the app/blots as that CHU. Stored in localStorage;
// applied on the next load. Ignored for non-admins.
const ADMIN_CHU_OVERRIDE_KEY = 'careflai_admin_chu_override'

export function getAdminCHUOverride(): string | null {
  return typeof window !== 'undefined' ? window.localStorage.getItem(ADMIN_CHU_OVERRIDE_KEY) : null
}

export function setAdminCHUOverride(key: string | null): void {
  if (typeof window === 'undefined') return
  if (key) window.localStorage.setItem(ADMIN_CHU_OVERRIDE_KEY, key)
  else window.localStorage.removeItem(ADMIN_CHU_OVERRIDE_KEY)
  window.location.reload()
}

export const CHUConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: profile } = useMyProfileWithModules()
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin'
  const override = isAdmin ? getAdminCHUOverride() : null

  const config = useMemo(
    () => (override ? resolveCHUConfig(override) : resolveCHUConfig(profile?.companyName)),
    [profile?.companyName, override]
  )

  return (
    <CHUConfigContext.Provider value={config}>
      {children}
    </CHUConfigContext.Provider>
  )
}

/** Access the resolved CHU config for the current user's organisation. */
export const useCHUConfig = (): CHUConfig => useContext(CHUConfigContext)

/** Shorthand — access only the features object. */
export const useCHUFeatures = () => useCHUConfig().features
